/**
 * Pure, feature-local derivations for `OperationalHealthScreen.tsx`
 * (REDISENO-PANEL.md §8.3). No React/I/O — same "Extract-Before-Mock"
 * convention every other v3 screen's own `derived.ts` already established
 * (`features/overview/derived.ts`, `features/telemetry/derived.ts`,
 * `features/campaigns/campaignDetailDerived.ts`): the tiny HH:mm formatter
 * is deliberately duplicated here rather than imported cross-feature, same
 * precedent those files document.
 */

import type { IntegrationStatus, OperationalHealthStatus } from "../../api/types";
import { formatDateTime } from "../../formatting/date";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Same calendar-day start convention as `campaigns/campaignDetailDerived.ts#startOfDayMs`, duplicated here per this feature's own Extract-Before-Mock precedent. */
function startOfDayMs(ms: number): number {
  const date = new Date(ms);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

/**
 * "Última verificación: hoy/ayer HH:MM", or an explicit locale date for
 * anything older. adv2-slice08 (FINDING R3-001): this used to ALWAYS say
 * "hoy" regardless of `generatedAtIso`'s real calendar day — the screen
 * keeps last-known data visible on a failed refresh (stale branch), so a
 * snapshot from a previous day was shown as "hoy HH:MM", falsely asserting
 * recency on a diagnostics screen. `nowMs` defaults to the real clock for
 * every real caller but lets tests inject a fixed instant (same convention
 * as `campaignDetailDerived.ts#relativeDayLabel`).
 */
export function formatVerificationLabel(generatedAtIso: string, nowMs: number = Date.now()): string {
  const date = new Date(generatedAtIso);
  if (Number.isNaN(date.getTime())) return "Última verificación: —";
  const days = Math.round((startOfDayMs(date.getTime()) - startOfDayMs(nowMs)) / DAY_MS);
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  if (days === 0) return `Última verificación: hoy ${time}`;
  if (days === -1) return `Última verificación: ayer ${time}`;
  return `Última verificación: ${formatDateTime(generatedAtIso)}`;
}

/** The two integrations §8.3 singles out as critical: "`database` o `acs-email` no disponible → rojo", overriding the general degraded->amber rule. */
const CRITICAL_INTEGRATION_KEYS = new Set(["database", "acs-email"]);

export type HealthBannerTone = "positive" | "warning" | "critical";

export interface HealthBanner {
  tone: HealthBannerTone;
  title: string;
  /** Only present for the amber "advertencias" case — the green/red banners are a single sentence (`title` alone). */
  message?: string;
}

/**
 * §8.3's banner rule, in priority order:
 * 1. `database` or `acs-email` not `available` (degraded OR pending) -> red.
 * 2. Else any OTHER integration `degraded`, OR the tenant identity itself
 *    `degraded` -> amber, naming what needs review.
 * 3. Else (every integration available and the identity too, or only a
 *    non-critical integration is merely `pending` — i.e. not yet
 *    implemented, not a problem) -> green.
 * A `pending` non-critical integration (e.g. a feature not shipped yet) is
 * deliberately NOT a banner-amber finding — the amber copy says
 * "necesitan revisión", which describes `degraded` (an actual problem), not
 * "not implemented yet".
 *
 * Codex review round 4 (P2, PR #387): a suspended tenant row (or the
 * identity query succeeding with no row) reports `identity.status ===
 * 'degraded'` while every integration, including the critical ones, stays
 * `available` -- this used to ignore `identity.status` entirely, producing
 * the green "Todo funciona correctamente" banner directly above a Cuenta
 * card whose own Estado row already shows the account needs attention.
 * `identityStatus` defaults to `"available"` so every existing
 * integrations-only caller is unaffected.
 */
export function healthBanner(
  integrations: readonly IntegrationStatus[],
  identityStatus: OperationalHealthStatus = "available",
): HealthBanner {
  const criticalDown = integrations.filter(
    (integration) => CRITICAL_INTEGRATION_KEYS.has(integration.key) && integration.status !== "available",
  );
  if (criticalDown.length > 0) {
    return {
      tone: "critical",
      title: "El envío de campañas podría no funcionar",
      message: "Revisa las integraciones marcadas abajo antes de programar un nuevo envío.",
    };
  }

  const degraded = integrations.filter((integration) => integration.status === "degraded");
  const identityDegraded = identityStatus === "degraded";
  if (degraded.length > 0 || identityDegraded) {
    const integrationsPhrase =
      degraded.length === 1 ? "1 integración" : degraded.length > 1 ? `${degraded.length} integraciones` : null;
    const subjects = [identityDegraded ? "la cuenta" : null, integrationsPhrase].filter(
      (subject): subject is string => subject !== null,
    );
    const verb = subjects.length > 1 || degraded.length > 1 ? "necesitan" : "necesita";
    return {
      tone: "warning",
      title: "Funcionando con advertencias",
      message: `El envío de campañas funciona. ${subjects.join(" y ")} ${verb} revisión…`,
    };
  }

  return { tone: "positive", title: "Todo funciona correctamente" };
}
